import React, { useState } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import Button from '../components/Shared/Button';
import { theme } from '../styles/theme';
import { useAssessment } from '../hooks/useAssessment';
import UserInfoForm from '../components/Assessment/UserInfoForm';
import { UserInfo } from '../types';

const HeroSection = styled.div`
  padding: 80px 0;
  text-align: center;
  background: linear-gradient(135deg, ${theme.colors.lightBlue} 0%, #fff 100%);
`;

const ContentContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
`;

const Title = styled.h1`
  font-family: ${theme.fonts.heading};
  font-size: 3rem;
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.medium};
  span {
    color: ${theme.colors.primary};
  }
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: ${theme.colors.secondary};
  margin-bottom: ${theme.spacing.xlarge};
  line-height: 1.6;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${theme.spacing.large};
  max-width: 1000px;
  margin: 60px auto;
  padding: 0 20px;
`;

const FeatureCard = styled.div`
  padding: ${theme.spacing.large};
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.05);
  text-align: left;
  border-top: 4px solid ${theme.colors.primary};

  h3 {
    margin-bottom: ${theme.spacing.small};
    color: ${theme.colors.text};
  }
  p {
    color: ${theme.colors.secondary};
    font-size: 0.95rem;
    line-height: 1.5;
  }
`;

const Home = () => {
  const history = useHistory();
  const { resetAssessment, setUserInfo } = useAssessment();
  const [showUserForm, setShowUserForm] = useState(false);

  const handleStart = () => {
    resetAssessment();
    setShowUserForm(true);
  };

  const handleUserInfoSubmit = (userInfo: UserInfo) => {
    setUserInfo(userInfo);
    history.push('/assessment');
  };

  const handleCancel = () => {
    setShowUserForm(false);
  };

  if (showUserForm) {
    return (
      <div className="container">
        <UserInfoForm onSubmit={handleUserInfoSubmit} onCancel={handleCancel} />
      </div>
    );
  }

  return (
    <div className="container">
      <HeroSection>
        <ContentContainer>
          <Title>Assess Your <span>AI Maturity</span></Title>
          <Subtitle>
            Discover where your organization stands in its AI adoption journey. 
            Get actionable insights across Strategy, Data, Technology, and People.
          </Subtitle>
          <Button onClick={handleStart} style={{ fontSize: '1.2rem', padding: '12px 32px' }}>
            Start Assessment
          </Button>
        </ContentContainer>
      </HeroSection>

      <FeaturesGrid>
        <FeatureCard>
          <h3>Comprehensive Analysis</h3>
          <p>Evaluate your readiness across 5 key dimensions including Strategy, Data, and Governance.</p>
        </FeatureCard>
        <FeatureCard>
          <h3>Instant Results</h3>
          <p>Get immediate feedback with a detailed visualization of your organization's maturity profile.</p>
        </FeatureCard>
        <FeatureCard>
          <h3>Actionable Insights</h3>
          <p>Understand your strengths and identify specific areas for improvement to advance your AI goals.</p>
        </FeatureCard>
      </FeaturesGrid>
    </div>
  );
};

export default Home;